import styled from '@emotion/styled';
import type { Order } from '@/domain/entities/order.entity';
import { formatCurrency } from './utils';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.sm};
`;

const Row = styled.div<{ bold?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  color: ${({ theme, bold }) => (bold ? theme.colors.text.primary : theme.colors.text.secondary)};
  font-weight: ${({ theme, bold }) =>
    bold ? theme.typography.config.weight.bold : theme.typography.config.weight.regular};

  ${({ bold, theme }) =>
    bold &&
    `
    padding-top: ${theme.space.sm};
    margin-top: ${theme.space.xs};
    border-top: 1px solid ${theme.colors.border.color};
    font-size: ${theme.fontSizes[3]}px;
  `}
`;

const Discount = styled.span`
  color: #2f806a;
`;

interface OrderSubtotalProps {
  readonly order: Order;
  readonly locale?: string;
  readonly currencyCode?: string;
  readonly currencySymbol?: string;
}

export function OrderSubtotal({
  order,
  locale = 'en-US',
  currencyCode = 'USD',
  currencySymbol,
}: OrderSubtotalProps) {
  const fmt = (amount: number) => formatCurrency(amount, locale, currencyCode, currencySymbol);

  const { totals } = order;
  const hasDiscount = totals.discounts.display > 0;
  const hasTaxes = totals.taxes.display > 0;
  const hasShipping = totals.shipping.display > 0;
  const hasFees = totals.fees.display > 0;
  const hasRefund = (order.total_refunded?.display ?? 0) > 0;

  return (
    <Container id="order-subtotal">
      <Row>
        <span>Subtotal ({order.line_items?.length ?? 0} items)</span>
        <span>{fmt(totals.items.display)}</span>
      </Row>

      {hasShipping && (
        <Row>
          <span>Shipping</span>
          <span>{fmt(totals.shipping.display)}</span>
        </Row>
      )}

      {!hasShipping && (
        <Row>
          <span>Shipping</span>
          <Discount>FREE</Discount>
        </Row>
      )}

      {hasTaxes && (
        <Row>
          <span>Tax</span>
          <span>{fmt(totals.taxes.display)}</span>
        </Row>
      )}

      {hasDiscount && (
        <Row>
          <span>Discount</span>
          <Discount>-{fmt(totals.discounts.display)}</Discount>
        </Row>
      )}

      {hasFees && (
        <Row>
          <span>Fees</span>
          <span>{fmt(totals.fees.display)}</span>
        </Row>
      )}

      {hasRefund && (
        <Row>
          <span>Refunded</span>
          <Discount>-{fmt(order.total_refunded!.display)}</Discount>
        </Row>
      )}

      <Row bold>
        <span>Total</span>
        <span>{fmt(totals.total.display)}</span>
      </Row>
    </Container>
  );
}
